<template>
  <div class="space-y-4">
    <TFormGroup
      :label-for="inputId"
      :label="t('delivery.address.label')"
      :is-invalid="isInvalid"
      :invalid-feedback="isInvalid ? t('errors.rules.required') : undefined"
    >
      <EstimatePlace
        :input-id="inputId"
        :destination="bookingEstimateParams.destination?.name"
        :input-placeholder="t('delivery.address.placeholder')"
        :countries="countries"
        @update-address="deliveryAddressUpdated"
      />
    </TFormGroup>

    <template v-if="errorCode === 'CROSS_BORDER_TRAVEL_DISALLOWED'">
      <EstimateCrossBorderTipbox :country="countryCode">
        <template v-if="hasNotElligibleButton">
          {{ t('notElligibleText') }}
        </template>
      </EstimateCrossBorderTipbox>

      <TButton
        v-if="hasNotElligibleButton"
        to="#similar-rvs"
      >
        {{ t('notElligibleButton') }}
      </TButton>
    </template>

    <template v-else>
      <TSubHeading v-if="!bookingEstimateParams.destination && !hideMaxDeliveryDistanceMessage">
        {{ t('delivery.enterDestination', { maxDeliveryDistance: formatDistanceWithUnit(deliveryMaxDistance ?? null) }) }}
      </TSubHeading>

      <TTipbox
        v-else-if="maximumDistanceOverrideWarning"
        variant="warning"
       
      >
        {{ t('delivery.outsideDeliveryRangeWarning', { distance: formatDistanceWithUnit(deliveryCostData?.Distance ?? null) }) }}
      </TTipbox>

      <TTipbox
        v-else-if="errorCode === 'DELIVERY_DISTANCE_EXCEEDS_MAX'"
        variant="warning"
       
      >
        {{ t('delivery.outsideDeliveryRangeBlock') }}
      </TTipbox>

      <TTipbox
        v-else-if="errorCode === 'NO_DELIVERY_DISTANCE_FOUND'"
        variant="warning"
      >
        {{ t('delivery.noDeliveryDistanceFound.warningText') }}
      </TTipbox>

      <TTipbox
        v-else-if="deliveryCostError"
        variant="warning"
       
      >
        {{ t('delivery.costUnavailable') }}
      </TTipbox>

      <template v-else-if="deliveryCostData">
        <TActionCard v-if="allowRequestToBook">
          <template #icon>
            <TIcon :icon="['fas', 'route']" />
          </template>
          {{
            t('delivery.distanceWithCostNote', {
              distance: formatDistanceWithUnit(deliveryCostData.Distance),
              fee: hasValidDeliveryInfoAndMinimumFee
                ? t('delivery.minimumFee', { deliveryCost: formatPrice(centsToDollars(deliveryCostData.CostCents)) })
                : t('delivery.fee', { deliveryCost: formatPrice(centsToDollars(deliveryCostData.CostCents)) }),
            })
          }}

          <template #button>
            <LazyEstimateAddDelivery
              v-if="bookingEstimateParams.destination && deliveryCostData"
              :is-delivered="bookingEstimateParams.rvUsage === 'Delivery'"
             
            />
          </template>
        </TActionCard>

        <TSubHeading v-else>
          {{ t('delivery.distanceNote', { distance: formatDistanceWithUnit(deliveryCostData.Distance) }) }}
        </TSubHeading>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { COUNTRY_SHORTS } from '~/constants'
import type { BookingEstimateDestination, Nullable } from '~/types'
import type { BackendErrorInterface } from '~/types/backend-api-errors'

const props = withDefaults(defineProps<{
  countryCode: COUNTRY_SHORTS
  measurementUnit: string
  deliveryMaxDistance: Nullable<number>
  deliveryMinimumCostCents: Nullable<number>
  allowRequestToBook?: boolean
  allowMaximumDistanceOverride?: boolean
  hasNotElligibleButton?: boolean
  isInvalid?: boolean
  countries?: COUNTRY_SHORTS[]
  hideMaxDeliveryDistanceMessage?: boolean
}>(), {
  countries: () => [],
})

const emit = defineEmits<{
  'delivery-address-updated': [destinationData?: BookingEstimateDestination]
}>()

const { t } = useI18n()
const inputId = useId()

const { bookingEstimateParams } = useBookingEstimate()

const { deliveryCostData, deliveryCostError } = useDeliveryCost()
const errorCode = computed(() => (deliveryCostError.value?.data as BackendErrorInterface)?.Code ?? '')

function formatDistanceWithUnit(distance: Nullable<number>) {
  if (distance === null) {
    return ''
  }

  return `${roundToNearestDecimal(distance, 0)} ${props.measurementUnit}`
}

const maximumDistanceOverrideWarning = computed(() => {
  if (!props.allowMaximumDistanceOverride) {
    return false
  }

  return (deliveryCostData.value?.Distance ?? 0) > (props.deliveryMaxDistance ?? 0)
})

const hasValidDeliveryInfoAndMinimumFee = computed(() => deliveryCostData.value?.CostCents === props.deliveryMinimumCostCents)

const { priceDisplay } = usePrice()
function formatPrice(value: number) {
  return priceDisplay({
    value,
    countryCode: props.countryCode,
    showCurrencyCode: true,
  })
}

function deliveryAddressUpdated(destinationData?: BookingEstimateDestination) {
  emit('delivery-address-updated', destinationData)
}
</script>

<i18n lang="json">
{
  "en": {
    "delivery": {
      "address": {
        "label": "Enter your delivery address",
        "placeholder": "Search for an address, campsite, city, town, or park"
      },
      "distanceNote": "{distance} away from the RV’s location",
      "distanceWithCostNote": "The delivery address is approximately {distance} from the RV location. {fee}",
      "fee": "Delivery is available for {deliveryCost}",
      "minimumFee": "The host has set a minimum delivery fee of {deliveryCost}.",
      "outsideDeliveryRangeWarning": "The address you entered is {distance} beyond the host’s maximum delivery range. Once you submit this change, it will be reviewed by the host. If accepted the extra delivery fee will be added to your total.",
      "outsideDeliveryRangeBlock": "The address entered is outside the delivery range for this RV. You can message the host to discuss delivery options.",
      "costUnavailable": "We couldn’t calculate delivery to this address. Try again or choose a different address.",
      "noDeliveryDistanceFound": {
        "warningText": "The RV cannot be delivered to this address. You can message the host to discuss delivery options."
      },
      "enterDestination": "The delivery fee will be calculated once you enter the delivery address. This host has a maximum delivery of {maxDeliveryDistance}."
    },
    "notElligibleButton": "Browse suitable RVs",
    "notElligibleText": "Let’s find an RV suitable for your trip!"
  },
  "fr": {
    "delivery": {
      "address": {
        "label": "Entrer votre adresse de livraison",
        "placeholder": "Rechercher une adresse, un site de camping, une ville ou un parc"
      },
      "distanceNote": "À {distance} de l’emplacement du VR",
      "distanceWithCostNote": "L’adresse de livraison se trouve à environ {distance} de l’emplacement du VR. {fee}",
      "fee": "La livraison est disponible pour {deliveryCost}",
      "minimumFee": "L’hôte a fixé des frais de livraison minimums de {deliveryCost}.",
      "outsideDeliveryRangeWarning": "L’adresse que vous avez entrée est située à {distance} au-delà de la plage de livraison maximale de l’hôte. Une fois que vous aurez soumis cette modification, elle sera examinée par l’hôte. Si elle est acceptée, des frais de livraison supplémentaires seront ajoutés à votre total.",
      "outsideDeliveryRangeBlock": "L’adresse ajoutée est en dehors de la plage de livraison pour ce VR. Vous pouvez envoyer un message à l’hôte pour discuter des options de livraison.",
      "costUnavailable": "Nous n’avons pas pu calculer la livraison vers cette adresse. Réessayez ou choisissez une autre adresse.",
      "noDeliveryDistanceFound": {
        "warningText": "Le VR ne peut pas être livré à cette adresse. Vous pouvez envoyer un message à l’hôte pour discuter des options de livraison."
      },
      "enterDestination": "Les frais de livraison seront calculés une fois que vous aurez ajouté l’adresse de livraison. Cet hôte a une livraison maximale de {maxDeliveryDistance}."
    },
    "notElligibleButton": "Parcourir les VR appropriés",
    "notElligibleText": "Trouvons un VR adapté à votre voyage!"
  }
}
</i18n>
